import { useGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { Jurisdiction } from '@dealstack/frontend/shared/jurisdictions';
import { JurisdictionType, LegalFormRes } from '@dealstack/shared/lib/generated';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

export interface JurisdictionOption {
	value: string;
	label: string;
}

export interface UseActiveLegalForms {
	findForJurisdictionName: (
		jurisdiction_name?: string,
		selectedJurisdictionLanguage?: string
	) => JurisdictionOption[];
}

export function useActiveLegalForms(jurisdictions: Jurisdiction[]): UseActiveLegalForms {
	const { assetsUrl } = useGlobalConfig();

	const activeLegalForms = useQuery({
		queryKey: ['activeLegalForms'],
		queryFn: async (): Promise<LegalFormRes[]> => {
			return fetch(`${assetsUrl}/context/countries/activeLegalFormsData.json`).then((res) =>
				res.json()
			);
		},
	});

	const findForJurisdictionName = useCallback(
		(jurisdiction_name?: string, selectedJurisdictionLanguage?: string) => {
			if (!jurisdiction_name) {
				return [];
			}

			const jurisdiction = jurisdictions.find(
				(j) => j.jurisdiction_name === jurisdiction_name
			);

			if (!jurisdiction) {
				return [];
			}

			const legalForms = getLegalFormsForJurisdiction(
				activeLegalForms.data ?? [],
				jurisdiction,
				selectedJurisdictionLanguage
			);
			return legalForms.map((legalForm) => ({
				value: legalForm.uniq_id_by_elf_code_and_language,
				label: legalForm.entity_legal_form_name_transliterated_name_per_ISO_01_140_10,
			}));
		},
		[activeLegalForms, jurisdictions]
	);

	return { findForJurisdictionName };
}

const getLegalFormsForJurisdiction = (
	activeLegalForms: LegalFormRes[],
	jurisdiction: Jurisdiction,
	selectedJurisdictionLanguage?: string
): LegalFormRes[] => {
	if (jurisdiction.type === JurisdictionType.COUNTRY_ONLY) {
		const legalForms = activeLegalForms.filter(
			(legalForm) => legalForm.country_code_ISO_3166_1 === jurisdiction.country_code
		);
		if (!selectedJurisdictionLanguage) {
			return legalForms;
		}

		return legalForms.filter(
			(legalForm) => legalForm.language === selectedJurisdictionLanguage
		);
	}

	if (jurisdiction.type === JurisdictionType.COUNTRY_AND_SUBDIVISION) {
		const countryCode = jurisdiction.country_code.substring(0, 2);
		const countryForms = activeLegalForms.filter(
			(legalForm) =>
				legalForm.country_code_ISO_3166_1 === countryCode &&
				!legalForm.country_sub_division_code_ISO_3166_2
		);

		const subDivisionForms = activeLegalForms.filter(
			(legalForm) =>
				legalForm.country_sub_division_code_ISO_3166_2 === jurisdiction.country_code
		);

		const legalForms = subDivisionForms.length ? subDivisionForms : countryForms;
		if (!selectedJurisdictionLanguage) {
			return legalForms;
		}

		return legalForms.filter(
			(legalForm) => legalForm.language === selectedJurisdictionLanguage
		);
	}

	return [];
};
